import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Phone, MessageSquare, Navigation, Loader2, Trash2, AlertTriangle } from "lucide-react";
import { api, API, errorText, formatAddress, formatPhone, mapsLink, routeLink } from "../lib/api";
import { STATUSES } from "../data/services";
import { STATUS_LABELS, SERVICE_LABELS, fmtDate, money, useAuth } from "./AuthContext";
import { StatusBadge } from "./Dashboard";

const Block = ({ title, children, testId }) => (
  <section className="panel p-5" data-testid={testId}>
    <h2 className="mb-3 text-xs font-bold uppercase tracking-[0.15em] text-gray-400">{title}</h2>
    {children}
  </section>
);

const Row = ({ k, v }) => (
  <div className="grid grid-cols-[130px_1fr] gap-2 py-1 text-sm"><dt className="text-gray-500">{k}</dt><dd className="text-white whitespace-pre-line break-words">{v || "—"}</dd></div>
);

const StatusPanel = ({ req, save }) => {
  const [status, setStatus] = useState(req.status);
  const [note, setNote] = useState("");
  const quick = [["contacted", "Mark contacted"], ["accepted", "Accept"], ["en_route", "En route"], ["completed", "Complete"]];
  return (
    <Block title="Status" testId="admin-status-panel">
      <div className="flex flex-wrap gap-2">
        {quick.map(([s, l]) => (
          <button key={s} type="button" disabled={req.status === s} onClick={() => save({ status: s })} className="btn-ghost px-3 py-1.5 text-xs disabled:opacity-40" data-testid={`admin-quick-${s}`}>{l}</button>
        ))}
        <button type="button" disabled={req.status === "cancelled"} onClick={() => save({ status: "cancelled", rejected: true, note: "Request rejected" })} className="rounded-full border border-red-500/60 px-3 py-1.5 text-xs font-semibold text-red-300 hover:bg-red-500/10 disabled:opacity-40" data-testid="admin-reject-btn">Reject</button>
      </div>
      <div className="mt-4 grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
        <select className="tow-input" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-status-select" aria-label="New status">
          {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
        </select>
        <input className="tow-input" placeholder="Note (optional)" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} data-testid="admin-status-note" />
        <button type="button" className="btn-yellow px-5 py-3 text-sm" onClick={() => { save({ status, note }); setNote(""); }} data-testid="admin-status-save-btn">Update</button>
      </div>
    </Block>
  );
};

const JobPanel = ({ req, save }) => {
  const [q, setQ] = useState(req.quoted_price ?? "");
  const [fp, setFp] = useState(req.final_price ?? "");
  const [dn, setDn] = useState(req.driver?.name || "");
  const [dp, setDp] = useState(req.driver?.phone ? formatPhone(req.driver.phone) : "");
  const num = (v) => (v === "" ? null : Number(v));
  return (
    <Block title="Quote, price & driver" testId="admin-job-panel">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm">Quoted price ($)<input type="number" min="0" step="0.01" inputMode="decimal" className="tow-input mt-1" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-quoted-price-input" /></label>
        <label className="text-sm">Final price ($)<input type="number" min="0" step="0.01" inputMode="decimal" className="tow-input mt-1" value={fp} onChange={(e) => setFp(e.target.value)} data-testid="admin-final-price-input" /></label>
        <label className="text-sm">Driver name<input className="tow-input mt-1" value={dn} maxLength={100} onChange={(e) => setDn(e.target.value)} data-testid="admin-driver-name-input" /></label>
        <label className="text-sm">Driver phone<input type="tel" className="tow-input mt-1" value={dp} onChange={(e) => setDp(e.target.value)} data-testid="admin-driver-phone-input" /></label>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button type="button" className="btn-yellow px-4 py-2 text-xs" data-testid="admin-save-quote-btn"
          onClick={() => save({ quoted_price: num(q), clear_quoted: q === "", ...(req.status === "new" || req.status === "contacted" ? { status: q === "" ? undefined : "quoted" } : {}) })}>Save quote</button>
        <button type="button" className="btn-ghost px-4 py-2 text-xs" data-testid="admin-save-final-btn" onClick={() => save({ final_price: num(fp), clear_final: fp === "" })}>Save final price</button>
        <button type="button" className="btn-ghost px-4 py-2 text-xs" data-testid="admin-assign-driver-btn"
          onClick={() => save({ driver: { name: dn, phone: dp }, ...(dn && ["new", "contacted", "quoted", "accepted"].includes(req.status) ? { status: "driver_assigned" } : {}) })}>Assign driver</button>
      </div>
      <p className="mt-3 text-xs text-gray-500">Quoted {money(req.quoted_price)} · Final {money(req.final_price)} · Driver {req.driver?.name || "—"}</p>
    </Block>
  );
};

const Notes = ({ req, setReq }) => {
  const [text, setText] = useState("");
  const add = async () => {
    if (!text.trim()) return;
    try {
      const { data } = await api.post(`/admin/requests/${req.id}/notes`, { text });
      setReq(data);
      setText("");
    } catch (e) { toast.error(errorText(e)); }
  };
  return (
    <Block title="Internal notes (never shown to customers)" testId="admin-notes-panel">
      <ul className="space-y-2">
        {req.internal_notes.map((n) => <li key={n.id} className="rounded-lg bg-[#0E0E12] p-3 text-sm"><p className="whitespace-pre-line">{n.text}</p><p className="mt-1 text-xs text-gray-500">{n.by} · {fmtDate(n.at)}</p></li>)}
      </ul>
      <textarea className="tow-input mt-3" rows={2} maxLength={2000} value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a note…" data-testid="admin-note-input" />
      <button type="button" className="btn-ghost mt-2 px-4 py-2 text-xs" onClick={add} data-testid="admin-add-note-btn">Add note</button>
    </Block>
  );
};

export default function RequestDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [req, setReq] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/admin/requests/${id}`).then((r) => setReq(r.data)).catch((e) => setError(errorText(e)));
  }, [id]);

  const save = async (patch) => {
    try {
      const { data } = await api.patch(`/admin/requests/${id}`, patch);
      setReq(data);
      toast.success("Request updated");
    } catch (e) { toast.error(errorText(e)); }
  };

  const remove = async () => {
    if (!window.confirm(`Permanently delete ${req.ref} and its customer data? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/requests/${id}`);
      toast.success("Request deleted");
      nav("/admin");
    } catch (e) { toast.error(errorText(e)); }
  };

  if (error) return <p className="text-red-400" role="alert">{error}</p>;
  if (!req) return <Loader2 className="h-6 w-6 animate-spin text-[#FACC15]" />;
  const v = req.vehicle;
  const c = req.customer;

  return (
    <div className="space-y-6" data-testid="admin-request-detail">
      <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white" data-testid="admin-back-btn"><ArrowLeft className="h-4 w-4" /> All requests</Link>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-mono text-3xl font-bold text-[#FACC15]" data-testid="admin-detail-ref">{req.ref}</h1>
        <StatusBadge status={req.status} />
        {req.rejected && <span className="text-xs font-bold uppercase text-red-400">Rejected</span>}
      </div>
      <p className="text-sm text-gray-400">{SERVICE_LABELS[req.service_id]} · Received {fmtDate(req.created_at)} · Language: {req.language === "es" ? "Español" : "English"}</p>
      {!v.safe_location && <p className="flex items-center gap-2 rounded-lg border border-red-500 bg-red-500/10 p-3 text-sm font-bold text-red-300" data-testid="admin-unsafe-flag"><AlertTriangle className="h-4 w-4" /> Customer reported the vehicle is NOT in a safe location.</p>}

      <div className="flex flex-wrap gap-2">
        <a href={`tel:${c.phone}`} className="btn-yellow px-4 py-2 text-sm" data-testid="admin-call-customer-btn"><Phone className="h-4 w-4" /> Call {formatPhone(c.phone)}</a>
        <a href={`sms:${c.phone}`} className="btn-ghost px-4 py-2 text-sm" data-testid="admin-text-customer-btn"><MessageSquare className="h-4 w-4" /> Text</a>
        <a href={mapsLink(req.pickup)} target="_blank" rel="noreferrer" className="btn-ghost px-4 py-2 text-sm" data-testid="admin-pickup-directions-btn"><Navigation className="h-4 w-4" /> Pickup directions</a>
        {req.destination && <a href={mapsLink(req.destination)} target="_blank" rel="noreferrer" className="btn-ghost px-4 py-2 text-sm" data-testid="admin-dest-directions-btn"><Navigation className="h-4 w-4" /> Destination directions</a>}
        {req.destination && <a href={routeLink(req.pickup, req.destination)} target="_blank" rel="noreferrer" className="btn-ghost px-4 py-2 text-sm" data-testid="admin-route-btn"><Navigation className="h-4 w-4" /> Pickup → destination route</a>}
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <div className="space-y-6">
          <Block title="Customer" testId="admin-customer-block">
            <dl><Row k="Name" v={c.name} /><Row k="Phone" v={formatPhone(c.phone)} /><Row k="Prefers" v={c.contact_method === "text" ? "Text message" : "Phone call"} /><Row k="Email" v={c.email} /><Row k="Contact notes" v={c.contact_notes} /><Row k="Consent" v={req.consent ? `Yes · ${fmtDate(req.consent_at)}` : "No"} /></dl>
          </Block>
          <Block title="Pickup" testId="admin-pickup-block">
            <dl><Row k="Address" v={formatAddress(req.pickup)} /><Row k="Instructions" v={req.pickup.notes} /><Row k="GPS" v={req.pickup.lat ? `${req.pickup.lat.toFixed(6)}, ${req.pickup.lng.toFixed(6)}` : ""} /></dl>
          </Block>
          <Block title="Destination" testId="admin-destination-block">
            {req.destination ? <dl><Row k="Address" v={formatAddress(req.destination)} /><Row k="Notes" v={req.destination.notes} /><Row k="GPS" v={req.destination.lat ? `${req.destination.lat.toFixed(6)}, ${req.destination.lng.toFixed(6)}` : ""} /></dl>
              : <p className="text-sm text-gray-300">{req.needs_destination_help ? "Customer needs help choosing a destination." : "No destination — on-site service."}</p>}
          </Block>
          <Block title="Vehicle" testId="admin-vehicle-block">
            <dl><Row k="Vehicle" v={[v.year, v.make, v.model].filter(Boolean).join(" ")} /><Row k="Color" v={v.color} /><Row k="Type" v={v.type} /><Row k="Safe location" v={v.safe_location ? "Yes" : "NO"} /><Row k="Special needs" v={v.special_requirements} /><Row k="Problem" v={v.description} /></dl>
            {req.photo_ids?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-3" data-testid="admin-photos">
                {req.photo_ids.map((p) => <a key={p} href={`${API}/admin/uploads/${p}`} target="_blank" rel="noreferrer"><img src={`${API}/admin/uploads/${p}`} alt={`Vehicle photo for ${req.ref}`} className="h-28 w-28 rounded-lg border border-[#2E2E3A] object-cover" /></a>)}
              </div>
            )}
          </Block>
        </div>
        <div className="space-y-6">
          <StatusPanel key={`s-${req.status}`} req={req} save={save} />
          <JobPanel key={`j-${req.updated_at}`} req={req} save={save} />
          <Notes req={req} setReq={setReq} />
          <Block title="Status history" testId="admin-history-panel">
            <ol className="space-y-3 border-l border-[#2E2E3A] pl-4">
              {[...req.history].reverse().map((h, i) => (
                <li key={`${h.at}-${i}`} className="text-sm">
                  <p className="font-semibold">{h.kind === "update" ? h.note : STATUS_LABELS[h.status]}</p>
                  {h.kind !== "update" && h.note && <p className="text-gray-400">{h.note}</p>}
                  <p className="text-xs text-gray-500">{fmtDate(h.at)} · {h.by}</p>
                </li>
              ))}
            </ol>
          </Block>
          <Block title="Notifications for this request" testId="admin-request-notifications">
            {req.notifications.length === 0 ? <p className="text-sm text-gray-400">No notifications attempted.</p> : (
              <ul className="space-y-1 text-sm">{req.notifications.map((n) => <li key={n.id}><span className="font-semibold">{n.kind.replace("_", " ")}</span> — <span className={n.status === "sent" ? "text-emerald-300" : n.status === "failed" ? "text-red-300" : "text-yellow-300"}>{n.status}</span> {n.error && <span className="text-gray-500">({n.error})</span>}</li>)}</ul>
            )}
          </Block>
          {user?.role === "owner" && <button type="button" onClick={remove} className="inline-flex items-center gap-2 text-sm text-red-400 hover:underline" data-testid="admin-delete-request-btn"><Trash2 className="h-4 w-4" /> Delete this request permanently</button>}
        </div>
      </div>
    </div>
  );
}
